import { useCallback, useEffect, useState } from 'react';
import { useNarrow } from './use-narrow';
import { useMobileAppInstalled, type MobileAppInstalled } from './mobile-app-status';
import { hasDismissedAppBanner, isIosSafari, markAppBannerDismissed } from './app-banner';
import { applyAppBannerQueryOverride, isAppBannerForced } from './dev-app-banner';

/**
 * Whether to show the custom mobile-web "get the app" banner, its CTA target's install
 * state, and how to dismiss it.
 *
 * Mirrors Apple's native Smart App Banner on the browsers that banner can't reach: it
 * shows regardless of install state and lets the CTA change (Open when the app is
 * installed, Get otherwise — see `AppBanner`), rather than only advertising to a
 * never-installed account. Gate: narrow viewport only (a phone, where one tap installs
 * or opens), not already dismissed in this browser, and NOT iOS Safari (Apple's own
 * banner covers that surface — see `isIosSafari`). `installed` is carried through for
 * the CTA label; it never gates the strip. Every client-only signal starts hidden and
 * resolves in an effect, so the strip never flashes on the first paint or during SSR.
 */
export function useShowAppBanner(): { show: boolean; installed: MobileAppInstalled; dismiss: () => void } {
  const narrow = useNarrow();
  const installed = useMobileAppInstalled();
  const [suppressed, setSuppressed] = useState(true);
  // Dev-only escape hatch (Dev Tools ▸ "Force app banner"): shows the strip regardless
  // of the real gate, for QA in any environment. Read in an effect so SSR stays hidden.
  const [forced, setForced] = useState(false);
  useEffect(() => {
    applyAppBannerQueryOverride();
    setSuppressed(hasDismissedAppBanner() || isIosSafari());
    setForced(isAppBannerForced());
  }, []);
  const dismiss = useCallback(() => {
    markAppBannerDismissed();
    setSuppressed(true);
  }, []);
  return { show: forced || (narrow && !suppressed), installed, dismiss };
}
